/**
 * Pure helpers that build query specs and apply client-side filter/sort/slice
 * for the per-member detail page (`/account/members/[memberId]`).
 *
 * Mirrors iOS `MemberDashboardView.swift` activity tab (lines 538-643): each
 * section is filtered to the current member and capped at 3 entries.
 *
 * The web client cannot rely on Firestore composite indexes for ad-hoc
 * `where + orderBy` combinations on `posts` / `responsibilities`, so these
 * helpers express a simple "fetch the parent collection, filter+sort+slice
 * client-side" strategy. That keeps the page resilient to missing indexes
 * and is consistent with the small per-household working set Zoey targets.
 *
 * Each helper is pure (no Firestore SDK imports) so it can be unit-tested
 * without touching the network. The page wires the resulting `path` strings
 * into `useFirestoreCollection` and applies `filter` + `sort` + `limit` to
 * the returned array.
 */

import { allLinkedEntityIds } from "./responsibility";
import { fromTimestamp } from "./firestore";
import type {
  AssignedChore,
  HouseholdPost,
  Responsibility,
} from "./models";

/**
 * Cap each activity list to match the iOS `MemberDashboardView` `prefix(3)`.
 */
export const MEMBER_ACTIVITY_LIMIT = 3;

export interface MemberQuerySpec {
  /** Firestore collection path to subscribe to. */
  path: string;
}

/**
 * Posts query spec: subscribe to all posts in the household, filter by
 * `created_by`, sort by `created_at` desc, slice to 3.
 *
 * Returns `null` when either argument is missing so the page can short-circuit.
 */
export function buildMemberPostsQuery(
  householdId: string | null | undefined,
  memberId: string | null | undefined,
): MemberQuerySpec | null {
  if (!householdId || !memberId) return null;
  return { path: `households/${householdId}/posts` };
}

/**
 * Responsibilities query spec: subscribe to all responsibilities in the
 * household, filter to those whose folded `linkedEntityIds`/`linkedEntityId`
 * contain the member, slice to 3.
 */
export function buildMemberResponsibilitiesQuery(
  householdId: string | null | undefined,
  memberId: string | null | undefined,
): MemberQuerySpec | null {
  if (!householdId || !memberId) return null;
  return { path: `households/${householdId}/responsibilities` };
}

/**
 * Assigned chores query spec: chores live at
 * `member_profiles/{memberId}/assigned_chores`. The path itself is keyed
 * to the member, so no filter is needed beyond slicing to 3.
 */
export function buildMemberChoresQuery(
  memberId: string | null | undefined,
): MemberQuerySpec | null {
  if (!memberId) return null;
  return { path: `member_profiles/${memberId}/assigned_chores` };
}

/**
 * Filters posts to those authored by `memberId`, sorts by `created_at` desc,
 * and slices to `MEMBER_ACTIVITY_LIMIT`.
 */
export function selectMemberPosts(
  posts: HouseholdPost[],
  memberId: string,
): HouseholdPost[] {
  return posts
    .filter((p) => p.created_by === memberId)
    .slice() // copy before sort to avoid mutating the listener's array
    .sort((a, b) => {
      const aTime = fromTimestamp(a.created_at)?.getTime() ?? 0;
      const bTime = fromTimestamp(b.created_at)?.getTime() ?? 0;
      return bTime - aTime;
    })
    .slice(0, MEMBER_ACTIVITY_LIMIT);
}

/**
 * Filters responsibilities to those linked to `memberId` via either the
 * legacy `linkedEntityId` or the new `linkedEntityIds`, then slices to 3.
 *
 * Order is preserved from the input array (responsibilities don't have a
 * single canonical sort field — iOS doesn't sort them in this view either).
 */
export function selectMemberResponsibilities(
  responsibilities: Responsibility[],
  memberId: string,
): Responsibility[] {
  return responsibilities
    .filter((r) => allLinkedEntityIds(r).includes(memberId))
    .slice(0, MEMBER_ACTIVITY_LIMIT);
}

/**
 * Slices assigned chores to `MEMBER_ACTIVITY_LIMIT`. Sorts by `assigned_at`
 * desc so the most recent chores surface first.
 *
 * No filter is needed because the collection path is already member-scoped.
 */
export function selectMemberChores(chores: AssignedChore[]): AssignedChore[] {
  return chores
    .slice()
    .sort((a, b) => {
      const aTime = fromTimestamp(a.assigned_at)?.getTime() ?? 0;
      const bTime = fromTimestamp(b.assigned_at)?.getTime() ?? 0;
      return bTime - aTime;
    })
    .slice(0, MEMBER_ACTIVITY_LIMIT);
}

/**
 * Maps a `MemberProfile.memberType` to a display label key.
 * Mirrors `MemberDashboardView.MemberCard.memberTypeBadge` (lines 218-226).
 */
export function memberTypeBadgeKey(memberType: string | undefined): string {
  switch (memberType) {
    case "pet":
      return "members.detail.badge.pet";
    case "child":
      return "members.detail.badge.child";
    case "elderly_parent":
      return "members.detail.badge.parent";
    case "guest":
      return "members.detail.badge.guest";
    default:
      return "members.detail.badge.adult";
  }
}
